// src/hooks/useUpstox.js
// FIXED: detects ?auth=success after Upstox redirect and re-checks token

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  upstoxAuth,
  upstoxMarket,
  upstoxScreener,
  UpstoxAuthError,
  NIFTY50_KEYS,
  FO_INDEX_KEYS,
} from '../../../services/auth-service/upstoxService';

// ─────────────────────────────────────────────
//  Generic async state
// ─────────────────────────────────────────────
function useAsync(fn) {
  const [state, setState] = useState({ data: null, loading: false, error: null });

  const execute = useCallback(async (...args) => {
    setState({ data: null, loading: true, error: null });
    try {
      const data = await fn(...args);
      setState({ data, loading: false, error: null });
      return data;
    } catch (err) {
      setState({ data: null, loading: false, error: err.message });
      throw err;
    }
  }, [fn]);

  return { ...state, execute };
}

// ─────────────────────────────────────────────
//  Main hook
// ─────────────────────────────────────────────
export function useUpstox() {
  const [authState, setAuthState] = useState({
    checked:       false,
    authenticated: false,
    expiresAt:     null,
  });

  // ── Core: call Django to check if token is valid ──
  const checkAuth = useCallback(() => {
    upstoxAuth.status()
      .then(res => {
        setAuthState({
          checked:       true,
          authenticated: res.authenticated,
          expiresAt:     res.expires_at ?? null,
        });

        // Clean ?auth=success from URL without a page reload
        if (res.authenticated) {
          const url = new URL(window.location.href);
          if (url.searchParams.has('auth')) {
            url.searchParams.delete('auth');
            window.history.replaceState({}, '', url.toString());
          }
        }
      })
      .catch(err => {
        console.warn('[useUpstox] auth status check failed:', err.message);
        setAuthState({ checked: true, authenticated: false, expiresAt: null });
      });
  }, []);

  // ── On mount: always check, and re-check if ?auth=success is in URL ──
  useEffect(() => {
    checkAuth();

    // If Upstox just redirected back, the token was just saved on Django.
    // Poll a couple of times to make sure the status call lands after Django
    // has finished writing the token (race condition on slow servers).
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth') === 'success') {
      const t1 = setTimeout(checkAuth, 800);
      const t2 = setTimeout(checkAuth, 2000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [checkAuth]);

  // ── When any call gets a 401 → mark logged out ──
  function handleAuthError(err) {
    if (err instanceof UpstoxAuthError) {
      setAuthState(s => ({ ...s, authenticated: false }));
    }
    throw err;
  }

  // ─────────────────────────────────────────────
  //  LTP POLLER — polls every intervalMs ms
  //  Usage:
  //    const { prices } = useLTPPoller(NIFTY50_KEYS, 5000);
  //    prices['NSE_EQ|INE002A01018']  →  2847
  // ─────────────────────────────────────────────
  function useLTPPoller(instrumentKeys = [], intervalMs = 5000) {
    const [prices, setPrices] = useState({});
    const [error,  setError]  = useState(null);
    const timerRef = useRef(null);
    const keysStr  = instrumentKeys.join(',');

    const poll = useCallback(async () => {
      if (!instrumentKeys.length) return;
      try {
        const res = await upstoxMarket.ltp(instrumentKeys);
        // Upstox response shape: { data: { 'NSE_EQ|...': { last_price: 2847 } } }
        const flat = {};
        Object.entries(res.data ?? {}).forEach(([k, v]) => {
          flat[k] = v.last_price;
        });
        setPrices(flat);
        setError(null);
      } catch (err) {
        setError(err.message);
        handleAuthError(err);
      }
    }, [keysStr]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      poll();
      timerRef.current = setInterval(poll, intervalMs);
      return () => clearInterval(timerRef.current);
    }, [poll, intervalMs]);

    return { prices, error, refresh: poll };
  }

  // ─────────────────────────────────────────────
  //  SCREENER SCAN
  // ─────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const screenerScan = useAsync(useCallback(async () => {
    const res = await upstoxScreener.batchQuotes(NIFTY50_KEYS).catch(handleAuthError);
    return res.data ?? [];
  }, []));

  // ─────────────────────────────────────────────
  //  HISTORICAL CANDLES
  // ─────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const candles = useAsync(useCallback(async (symbol, interval, from, to) => {
    const res = await upstoxMarket.candles(symbol, interval, from, to).catch(handleAuthError);
    return (res.data?.candles ?? []).map(c => ({
      time: c[0], open: c[1], high: c[2], low: c[3], close: c[4], volume: c[5], oi: c[6],
    }));
  }, []));

  // ─────────────────────────────────────────────
  //  INTRADAY CANDLES
  // ─────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const intradayCandles = useAsync(useCallback(async (symbol, interval = '1minute') => {
    const res = await upstoxMarket.intradayCandles(symbol, interval).catch(handleAuthError);
    return (res.data?.candles ?? []).map(c => ({
      time: c[0], open: c[1], high: c[2], low: c[3], close: c[4], volume: c[5],
    }));
  }, []));

  // ─────────────────────────────────────────────
  //  OPTION CHAIN
  // ─────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const optionChain = useAsync(useCallback(async (index = 'NIFTY', expiry) => {
    const symbol = FO_INDEX_KEYS[index];
    const res    = await upstoxMarket.optionChain(symbol, expiry).catch(handleAuthError);
    return res.data ?? [];
  }, []));

  // ─────────────────────────────────────────────
  //  INSTRUMENT SEARCH
  // ─────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const search = useAsync(useCallback(async (query, exchange = 'NSE_EQ') => {
    const res = await upstoxMarket.search(query, exchange).catch(handleAuthError);
    return res.data ?? [];
  }, []));

  // ─────────────────────────────────────────────
  //  FULL QUOTE
  // ─────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quote = useAsync(useCallback(async (keys) => {
    const res = await upstoxMarket.quote(keys).catch(handleAuthError);
    return res.data ?? {};
  }, []));

  return {
    auth: {
      ...authState,
      login:      upstoxAuth.login,
      checkAuth,  // expose so any component can manually re-check
      logout: async () => {
        await upstoxAuth.logout();
        setAuthState({ checked: true, authenticated: false, expiresAt: null });
      },
    },
    screenerScan,
    candles,
    intradayCandles,
    optionChain,
    search,
    quote,
    useLTPPoller,
  };
}